import { useEffect, useState, useCallback, useRef } from 'react';
import { siteHistoryApi, siteApi, workerApi, safetyKitApi, stationeryApi, itApi, materialUnitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import MaterialInfoPanel from '../../components/common/MaterialInfoPanel';
import SearchableSelect from '../../components/common/SearchableSelect';
import { MATERIAL_TYPES, materialTypeBadgeClass, materialTypeLabel } from '../../constants/materialTypes';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtVal(n) {
  const num = Number(n);
  if (isNaN(num)) return '—';
  return '₹' + num.toLocaleString('en-IN');
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

// ── MultiSelect component ─────────────────────────────────────────────────────
// mode: 'all' | 'none' | 'custom'   selectedIds: Set<number>
// Shows "All [label]s" when mode='all', "N selected" when 'custom', "No [label]s" (amber) when 'none'

function MultiSelect({ label, items, mode, selectedIds, onModeChange, onIdsChange, loading: itemsLoading }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = items.filter(it =>
    !search || it.name.toLowerCase().includes(search.toLowerCase())
  );

  const isAll  = mode === 'all';
  const isNone = mode === 'none' || (mode === 'custom' && selectedIds.size === 0);

  const btnLabel = itemsLoading
    ? 'Loading…'
    : isAll
    ? `All ${label}s`
    : isNone
    ? `No ${label}s selected`
    : `${selectedIds.size} of ${items.length} ${label}${selectedIds.size === 1 ? '' : 's'} selected`;

  const handleToggleAll = () => {
    if (isAll) {
      onModeChange('none');
      onIdsChange(new Set());
    } else {
      onModeChange('all');
      onIdsChange(new Set());
    }
  };

  const handleToggleItem = (id) => {
    let newSet;
    if (isAll) {
      // Deselect one from "all" → custom mode with everyone except this
      newSet = new Set(items.map(i => i.id));
      newSet.delete(id);
    } else {
      newSet = new Set(selectedIds);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    }

    if (newSet.size === 0)          { onModeChange('none');   onIdsChange(new Set()); }
    else if (newSet.size === items.length) { onModeChange('all');    onIdsChange(new Set()); }
    else                             { onModeChange('custom'); onIdsChange(newSet); }
  };

  const isItemChecked = (id) => isAll || selectedIds.has(id);

  return (
    <div className="relative" ref={ref}>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      <button
        type="button"
        onClick={() => !itemsLoading && setOpen(o => !o)}
        className={`input w-full flex items-center justify-between text-left gap-2 ${
          isNone ? 'border-amber-400 bg-amber-50' : ''
        }`}
      >
        <span className={`text-sm truncate flex-1 ${isNone ? 'text-amber-700 font-medium' : 'text-slate-700'}`}>
          {btnLabel}
        </span>
        <svg className="w-4 h-4 text-slate-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
        </svg>
      </button>

      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-full min-w-[220px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          {/* Search — only if enough items */}
          {items.length > 7 && (
            <div className="p-2 border-b border-slate-100">
              <input
                autoFocus
                type="text"
                placeholder="Search…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onClick={e => e.stopPropagation()}
                className="input text-sm py-1 w-full"
              />
            </div>
          )}

          {/* All [label]s row */}
          <div className="p-1.5 border-b border-slate-100">
            <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-indigo-50 select-none">
              <input
                type="checkbox"
                checked={isAll}
                onChange={handleToggleAll}
                className="rounded text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-semibold text-slate-800">All {label}s</span>
              <span className="ml-auto text-[10px] text-slate-400">{items.length} total</span>
            </label>
          </div>

          {/* Individual items */}
          <div className="max-h-48 overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No matches</p>
            ) : filtered.map(item => (
              <label
                key={item.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50 select-none"
              >
                <input
                  type="checkbox"
                  checked={isItemChecked(item.id)}
                  onChange={() => handleToggleItem(item.id)}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-slate-700 flex-1">{item.name}</span>
                {item.category && (
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                    item.category === 'CONSUMABLE' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'
                  }`}>{item.category}</span>
                )}
              </label>
            ))}
          </div>

          {/* Footer summary */}
          <div className="border-t border-slate-100 px-3 py-1.5 bg-slate-50 flex items-center justify-between">
            <span className="text-[10px] text-slate-400">
              {isAll ? `All ${items.length} selected` : isNone ? 'None selected' : `${selectedIds.size} selected`}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[10px] font-semibold text-indigo-600 hover:underline"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Badges ────────────────────────────────────────────────────────────────────

function TxTypeBadge({ type }) {
  const cfg = {
    material_issue:    { cls: 'bg-indigo-600 text-white',                              label: '↑ Mat Issue' },
    material_return:   { cls: 'bg-indigo-50 text-indigo-700 border border-indigo-300', label: '↓ Mat Return' },
    material_addition: { cls: 'bg-green-600 text-white',                               label: '+ Mat Added' },
    material_edit:     { cls: 'bg-amber-50 text-amber-700 border border-amber-300',    label: '✎ Mat Edited' },
    material_damage:   { cls: 'bg-red-100 text-red-700 border border-red-300',         label: '⚠ Damage' },
    stationery_issue:  { cls: 'bg-violet-600 text-white',                              label: '↑ Stat Issue' },
    stationery_return: { cls: 'bg-violet-50 text-violet-700 border border-violet-300', label: '↓ Stat Return' },
    stationery_damage: { cls: 'bg-orange-100 text-orange-700 border border-orange-300', label: '⚠ Stat Damage' },
    it_issue:          { cls: 'bg-cyan-600 text-white',                                label: '↑ IT Issue' },
    it_return:         { cls: 'bg-cyan-50 text-cyan-700 border border-cyan-300',       label: '↓ IT Return' },
    it_damage:         { cls: 'bg-rose-100 text-rose-700 border border-rose-300',      label: '⚠ IT Damage' },
    worker_assignment: { cls: 'bg-sky-600 text-white',                                 label: '→ Assigned' },
    worker_transfer:   { cls: 'bg-teal-600 text-white',                                label: '⇄ Transferred' },
    damage_approval:   { cls: 'bg-amber-100 text-amber-800 border border-amber-300',   label: '⚠ Damage Approval' },
  };
  const c = cfg[type] || { cls: 'bg-gray-100 text-gray-600', label: type };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${c.cls}`}>{c.label}</span>;
}

function StatusBadge({ status }) {
  const cfg = { active:'bg-green-100 text-green-700', returned:'bg-gray-100 text-gray-600', under_repair:'bg-amber-100 text-amber-700', repaired:'bg-teal-100 text-teal-700', scrapped:'bg-red-100 text-red-700', damaged:'bg-red-100 text-red-700', assigned:'bg-sky-100 text-sky-700', transferred:'bg-teal-100 text-teal-700', pending_approval:'bg-amber-100 text-amber-700', approved:'bg-red-100 text-red-700', rejected:'bg-gray-100 text-gray-500', added:'bg-green-100 text-green-700', edited:'bg-amber-100 text-amber-700' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${cfg[status]||'bg-gray-100 text-gray-500'}`}>{status?status.replace(/_/g,' '):'—'}</span>;
}

// ── Pagination ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 25;

function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-gray-400 text-xs">Page {page} of {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onChange(page - 1)} disabled={page === 1}
          className="px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
          ← Prev
        </button>
        {Array.from({ length: Math.min(total, 7) }, (_, i) => {
          const p = total <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= total - 3 ? total - 6 + i : page - 3 + i;
          return (
            <button key={p} onClick={() => onChange(p)}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${p === page ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-gray-50'}`}>
              {p}
            </button>
          );
        })}
        <button onClick={() => onChange(page + 1)} disabled={page === total}
          className="px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors">
          Next →
        </button>
      </div>
    </div>
  );
}

const IS_MATERIAL_TYPE = new Set(MATERIAL_TYPES.map((t) => t.value));

function CategoryBadge({ category }) {
  if (!category) return null;
  if (IS_MATERIAL_TYPE.has(category)) {
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${materialTypeBadgeClass(category)}`}>{materialTypeLabel(category)}</span>;
  }
  const cfg = { CONSUMABLE:'bg-orange-100 text-orange-700', DURABLE:'bg-blue-100 text-blue-700' };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg[category]||'bg-gray-100 text-gray-500'}`}>{category}</span>;
}

// ── Analytics Card ────────────────────────────────────────────────────────────

// Shows current site status, not period activity — Inventory is what's on
// the shelf right now, Issued is what's currently checked out and not yet
// returned. Both are live snapshots (see siteHistory.js's "Current status"
// query), not totals accumulated over the selected date range.
const AnalyticsCard = ({ title, icon, color, inventory, issued }) => (
  <div className={`card border-l-4 ${color}`}>
    <div className="flex items-center gap-2 mb-3">
      <span className="text-xl">{icon}</span>
      <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-blue-600 mb-1">Inventory</p>
        <p className="text-lg font-bold text-blue-800">{inventory.qty ?? 0} <span className="text-xs font-normal">units</span></p>
        <p className="text-xs text-blue-600 font-medium">{fmtVal(inventory.value ?? 0)}</p>
      </div>
      <div className="bg-green-50 rounded-lg p-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-green-600 mb-1">Issued</p>
        <p className="text-lg font-bold text-green-800">{issued.qty ?? 0} <span className="text-xs font-normal">units</span></p>
        <p className="text-xs text-green-600 font-medium">{fmtVal(issued.value ?? 0)}</p>
      </div>
    </div>
  </div>
);

// ── Default analytics ─────────────────────────────────────────────────────────

const ZERO = { inventoryQty:0, inventoryValue:0, issuedQty:0, issuedValue:0 };
const defaultAnalytics = {
  materials: {...ZERO},
  stationery: { consumable:{...ZERO}, durable:{...ZERO}, total:{...ZERO} },
  it:         { consumable:{...ZERO}, durable:{...ZERO}, total:{...ZERO} },
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function SiteHistory() {
  const { user, hasPermission, isSiteRestricted: isStorekeeper } = useAuth();
  // IT is a third item pillar isolated behind it_view — a user without it
  // must not see any IT filter option, item in a picker, analytics card, or
  // table columns, mirroring InventoryPage.jsx's canViewIT gating pattern.
  const canViewIT = hasPermission('it_view');

  // Click-and-drag horizontal scrolling for the transactions table
  const txScrollRef = useRef(null);
  const dragRef = useRef({ dragging: false, startX: 0, scrollLeft: 0 });

  const handleTxMouseDown = useCallback((e) => {
    const el = txScrollRef.current;
    if (!el) return;
    dragRef.current = { dragging: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
  }, []);

  const handleTxMouseMove = useCallback((e) => {
    const state = dragRef.current;
    const el = txScrollRef.current;
    if (!state.dragging || !el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - state.startX;
    el.scrollLeft = state.scrollLeft - walk;
  }, []);

  const handleTxMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
  }, []);

  // Date / basic filters
  const [fromDate,     setFromDate]     = useState(() => daysAgoStr(30));
  const [toDate,       setToDate]       = useState(() => todayStr());
  const [siteFilter,   setSiteFilter]   = useState('');
  const [workerFilter, setWorkerFilter] = useState('');
  const [txType,       setTxType]       = useState('all');
  const [category,     setCategory]     = useState('all');
  const [materialType, setMaterialType] = useState('all');

  // Material multi-select state
  const [matMode,        setMatMode]        = useState('all');  // 'all' | 'none' | 'custom'
  const [selectedKitIds, setSelectedKitIds] = useState(new Set());

  // Stationery multi-select state
  const [statMode,         setStatMode]         = useState('all');
  const [selectedItemIds,  setSelectedItemIds]  = useState(new Set());

  // IT multi-select state — mirrors Stationery's, only relevant when canViewIT
  const [itMode,           setItMode]           = useState('all');
  const [selectedItItemIds, setSelectedItItemIds] = useState(new Set());

  // Material ID lifecycle history modal (same pattern as InventoryPage.jsx)
  const [historyModal, setHistoryModal] = useState(null); // { unit, events } | 'loading'
  const openMaterialHistory = async (materialUnitId) => {
    setHistoryModal('loading');
    try {
      const res = await materialUnitApi.history(materialUnitId);
      setHistoryModal(res.data.data);
    } catch {
      setHistoryModal(null);
    }
  };

  // Data
  const [transactions, setTransactions] = useState([]);
  const [analytics,    setAnalytics]    = useState(defaultAnalytics);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [page,         setPage]         = useState(1);

  // Reference data
  const [sites,     setSites]     = useState([]);
  const [workers,   setWorkers]   = useState([]);
  const [kits,      setKits]      = useState([]);
  const [statItems, setStatItems] = useState([]);
  const [itItems,   setItItems]   = useState([]);
  const [refLoading, setRefLoading] = useState(true);

  // Load reference data once
  useEffect(() => {
    const loads = [
      workerApi.list().then(r => setWorkers(r.data.data || [])).catch(() => {}),
      safetyKitApi.list().then(r => setKits(r.data.data || [])).catch(() => {}),
      stationeryApi.listItems().then(r => setStatItems(r.data.data || [])).catch(() => {}),
    ];
    if (!isStorekeeper) {
      loads.push(siteApi.list().then(r => setSites(r.data.data || [])).catch(() => {}));
    }
    // IT item list — only fetched when it_view is held; never issued at all
    // otherwise (same "don't even ask" rule as InventoryPage.jsx's canViewIT).
    if (canViewIT) {
      loads.push(itApi.listItems().then(r => setItItems(r.data.data || [])).catch(() => {}));
    }
    Promise.all(loads).finally(() => setRefLoading(false));
  }, [isStorekeeper, canViewIT]);

  // Stable keys for Set-based deps (Set identity doesn't trigger useEffect)
  const matKey  = matMode  === 'custom' ? Array.from(selectedKitIds).sort().join(',')  : matMode;
  const statKey = statMode === 'custom' ? Array.from(selectedItemIds).sort().join(',') : statMode;
  const itKey   = itMode   === 'custom' ? Array.from(selectedItItemIds).sort().join(',') : itMode;

  // Fetch history on filter change
  useEffect(() => {
    const params = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate)   params.toDate   = toDate;
    if (!isStorekeeper && siteFilter) params.siteId   = siteFilter;
    if (workerFilter)                 params.workerId = workerFilter;
    if (txType   !== 'all')           params.txType   = txType;
    if (category !== 'all')           params.category = category;
    if (materialType !== 'all')       params.materialType = materialType;

    // Material filter
    const matIsNone = matMode === 'none' || (matMode === 'custom' && selectedKitIds.size === 0);
    if (matIsNone) {
      params.kitIds = 'none';
    } else if (matMode === 'custom' && selectedKitIds.size > 0) {
      params.kitIds = Array.from(selectedKitIds).join(',');
    }
    // matMode === 'all' → no param → backend includes all

    // Stationery filter
    const statIsNone = statMode === 'none' || (statMode === 'custom' && selectedItemIds.size === 0);
    if (statIsNone) {
      params.itemIds = 'none';
    } else if (statMode === 'custom' && selectedItemIds.size > 0) {
      params.itemIds = Array.from(selectedItemIds).join(',');
    }

    // IT filter — only sent when canViewIT (mirrors the item-picker itself
    // never being populated for a user without it_view); the backend also
    // independently gates IT on it_view server-side, this is just defense
    // in depth / avoiding a pointless param on the request.
    if (canViewIT) {
      const itIsNone = itMode === 'none' || (itMode === 'custom' && selectedItItemIds.size === 0);
      if (itIsNone) {
        params.itItemIds = 'none';
      } else if (itMode === 'custom' && selectedItItemIds.size > 0) {
        params.itItemIds = Array.from(selectedItItemIds).join(',');
      }
    }

    setLoading(true);
    setError('');
    siteHistoryApi.list(params)
      .then(r => {
        const d = r.data.data || {};
        setTransactions(d.transactions || []);
        setPage(1);
        const a = d.analytics || {};
        setAnalytics({
          materials: { ...ZERO, ...(a.materials || {}) },
          stationery: {
            consumable: { ...ZERO, ...(a.stationery?.consumable || {}) },
            durable:    { ...ZERO, ...(a.stationery?.durable    || {}) },
            total:      { ...ZERO, ...(a.stationery?.total      || {}) },
          },
          it: {
            consumable: { ...ZERO, ...(a.it?.consumable || {}) },
            durable:    { ...ZERO, ...(a.it?.durable    || {}) },
            total:      { ...ZERO, ...(a.it?.total      || {}) },
          },
        });
      })
      .catch(err => {
        setError(err?.response?.data?.message || 'Failed to load history.');
        setTransactions([]);
        setAnalytics(defaultAnalytics);
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate, siteFilter, workerFilter, txType, category, materialType, matKey, statKey, itKey, isStorekeeper, canViewIT]); // eslint-disable-line

  // Quick range
  const applyRange = useCallback((range) => {
    const today = todayStr();
    const map = { '7d':7, '30d':30, '3m':90, '6m':180, '1y':365 };
    if (map[range]) { setFromDate(daysAgoStr(map[range])); setToDate(today); return; }
    if (range === 'ytd') { setFromDate(`${new Date().getFullYear()}-01-01`); setToDate(today); }
  }, []);

  const colSpan     = isStorekeeper ? 10 : 11;
  const mat         = analytics.materials;
  const statCons    = analytics.stationery.consumable;
  const statDur     = analytics.stationery.durable;
  const itCons      = analytics.it.consumable;
  const itDur       = analytics.it.durable;
  const totalPages  = Math.max(1, Math.ceil(transactions.length / PAGE_SIZE));
  const pagedTxns   = transactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Show a warning chip when either type is excluded
  const matIsNone  = matMode  === 'none' || (matMode  === 'custom' && selectedKitIds.size  === 0);
  const statIsNone = statMode === 'none' || (statMode === 'custom' && selectedItemIds.size === 0);
  const itIsNone   = canViewIT && (itMode === 'none' || (itMode === 'custom' && selectedItItemIds.size === 0));

  return (
    <div className="space-y-5">
      <PageHeader title="Site History" subtitle="Comprehensive inventory movement history and audit trail" />

      {/* ── Filter Panel ─────────────────────────────────────────────────────── */}
      <div className="card space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Filters</h2>
          {(matIsNone || statIsNone || itIsNone) && (
            <div className="flex items-center gap-2">
              {matIsNone  && <span className="text-[11px] bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">Materials hidden</span>}
              {statIsNone && <span className="text-[11px] bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">Stationery hidden</span>}
              {itIsNone   && <span className="text-[11px] bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-full">IT hidden</span>}
            </div>
          )}
        </div>

        {/* Row 1: dates + quick range pills */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From Date</label>
            <input type="date" value={fromDate} max={toDate} onChange={e => { setFromDate(e.target.value); }} className="input w-full text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To Date</label>
            <input type="date" value={toDate} min={fromDate} max={todayStr()} onChange={e => { setToDate(e.target.value); }} className="input w-full text-sm" />
          </div>
          {!isStorekeeper && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Site</label>
              <SearchableSelect
                className="input w-full text-sm"
                options={[{ id: '', label: 'All Sites' }, ...sites.map(s => ({ id: s.id, label: s.name }))]}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder="All Sites"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Worker</label>
            <select value={workerFilter} onChange={e => setWorkerFilter(e.target.value)} className="input w-full text-sm">
              <option value="">All Workers</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.name}{w.employeeId ? ` (${w.employeeId})` : ''}</option>)}
            </select>
          </div>
        </div>

        {/* Quick range pills — directly tied to the date inputs above */}
        <div className="flex items-center gap-2 flex-wrap pb-0.5 border-b border-slate-100">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mr-1">Quick Select:</span>
          {[{l:'7D',v:'7d'},{l:'30D',v:'30d'},{l:'3M',v:'3m'},{l:'6M',v:'6m'},{l:'1Y',v:'1y'},{l:'YTD',v:'ytd'}].map(({l,v}) => {
            const today = todayStr();
            const active = (() => {
              if (toDate !== today) return false;
              if (v==='7d')  return fromDate === daysAgoStr(7);
              if (v==='30d') return fromDate === daysAgoStr(30);
              if (v==='3m')  return fromDate === daysAgoStr(90);
              if (v==='6m')  return fromDate === daysAgoStr(180);
              if (v==='1y')  return fromDate === daysAgoStr(365);
              if (v==='ytd') return fromDate === `${new Date().getFullYear()}-01-01`;
              return false;
            })();
            return (
              <button key={v} type="button" onClick={() => applyRange(v)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-300 border border-transparent'
                }`}>
                {l}
              </button>
            );
          })}
        </div>

        {/* Row 2: material multi-select, stationery multi-select, [IT multi-select], tx type, category, material type */}
        <div className={`grid grid-cols-2 gap-3 ${canViewIT ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          <MultiSelect
            label="Material"
            items={kits}
            mode={matMode}
            selectedIds={selectedKitIds}
            onModeChange={setMatMode}
            onIdsChange={setSelectedKitIds}
            loading={refLoading}
          />
          <MultiSelect
            label="Stationery"
            items={statItems}
            mode={statMode}
            selectedIds={selectedItemIds}
            onModeChange={setStatMode}
            onIdsChange={setSelectedItemIds}
            loading={refLoading}
          />
          {/* IT item picker — entirely absent from the DOM (not just disabled)
              for a user without it_view, per this app's hide-not-disable
              convention for it_view-gated UI (see InventoryPage.jsx). */}
          {canViewIT && (
            <MultiSelect
              label="IT"
              items={itItems}
              mode={itMode}
              selectedIds={selectedItItemIds}
              onModeChange={setItMode}
              onIdsChange={setSelectedItItemIds}
              loading={refLoading}
            />
          )}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Transaction Type</label>
            <select value={txType} onChange={e => setTxType(e.target.value)} className="input w-full text-sm">
              <option value="all">All Types</option>
              <option value="material_issue">Material Issue</option>
              <option value="material_return">Material Return</option>
              <option value="material_addition">Material Added</option>
              <option value="material_edit">Material Edited</option>
              <option value="material_damage">Material Damage</option>
              <option value="stationery_issue">Stationery Issue</option>
              <option value="stationery_return">Stationery Return</option>
              <option value="stationery_damage">Stationery Damage</option>
              {canViewIT && <option value="it_issue">IT Issue</option>}
              {canViewIT && <option value="it_return">IT Return</option>}
              {canViewIT && <option value="it_damage">IT Damage</option>}
              <option value="worker_assignment">Worker Assigned</option>
              <option value="worker_transfer">Worker Transferred</option>
              <option value="damage_approval">Damage Approval</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)} className="input w-full text-sm">
              <option value="all">All Categories</option>
              <option value="CONSUMABLE">Consumable</option>
              <option value="DURABLE">Durable</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Material Type</label>
            <select value={materialType} onChange={e => setMaterialType(e.target.value)} className="input w-full text-sm">
              <option value="all">All Types</option>
              {MATERIAL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Error ────────────────────────────────────────────────────────────── */}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

      {/* ── Analytics Cards ───────────────────────────────────────────────────── */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${canViewIT ? 'lg:grid-cols-5' : ''}`}>
        <AnalyticsCard title="Materials" icon="📦" color="border-l-indigo-500"
          inventory={{ qty: mat.inventoryQty, value: mat.inventoryValue }}
          issued={{ qty: mat.issuedQty, value: mat.issuedValue }} />
        <AnalyticsCard title="Stationery (Consumable)" icon="📝" color="border-l-orange-500"
          inventory={{ qty: statCons.inventoryQty, value: statCons.inventoryValue }}
          issued={{ qty: statCons.issuedQty, value: statCons.issuedValue }} />
        <AnalyticsCard title="Stationery (Durable)" icon="🔧" color="border-l-violet-500"
          inventory={{ qty: statDur.inventoryQty, value: statDur.inventoryValue }}
          issued={{ qty: statDur.issuedQty, value: statDur.issuedValue }} />
        {/* IT analytics cards — entirely absent for a user without it_view,
            same hide-not-disable rule as the IT item picker above. */}
        {canViewIT && (
          <AnalyticsCard title="IT (Consumable)" icon="🖨️" color="border-l-cyan-500"
            inventory={{ qty: itCons.inventoryQty, value: itCons.inventoryValue }}
            issued={{ qty: itCons.issuedQty, value: itCons.issuedValue }} />
        )}
        {canViewIT && (
          <AnalyticsCard title="IT (Durable)" icon="💻" color="border-l-sky-500"
            inventory={{ qty: itDur.inventoryQty, value: itDur.inventoryValue }}
            issued={{ qty: itDur.issuedQty, value: itDur.issuedValue }} />
        )}
      </div>

      {/* ── Transaction Table ─────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-700">Transactions</h3>
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
              {transactions.length}
            </span>
          </div>
          <p className="text-xs text-slate-400">{fmtDate(fromDate)} – {fmtDate(toDate)}</p>
        </div>

        {loading ? <LoadingSpinner /> : (
          <div
            ref={txScrollRef}
            className="overflow-x-auto select-none cursor-grab active:cursor-grabbing"
            onMouseDown={handleTxMouseDown}
            onMouseMove={handleTxMouseMove}
            onMouseUp={handleTxMouseUp}
            onMouseLeave={handleTxMouseUp}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Date', ...(isStorekeeper ? [] : ['Site']), 'Type', 'Item', 'Category', 'Worker', 'Qty', 'Unit Price', 'Value (₹)', 'Status', 'Issued By'].map(h => (
                    <th key={h} className={`px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap ${['Qty','Unit Price','Value (₹)'].includes(h)?'text-right':'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center py-12 text-slate-400 text-sm">
                      {matIsNone && statIsNone
                        ? 'No data — both Materials and Stationery are hidden. Select items using the filters above.'
                        : matIsNone
                        ? 'Materials are hidden — showing Stationery only. No Stationery transactions found.'
                        : statIsNone
                        ? 'Stationery is hidden — showing Materials only. No Material transactions found.'
                        : 'No transactions found for the selected filters'}
                    </td>
                  </tr>
                ) : pagedTxns.map(t => (
                  <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(t.date)}</td>
                    {!isStorekeeper && (
                      <td className="px-3 py-2.5">
                        <span className="inline-block px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600">{t.siteName||'—'}</span>
                      </td>
                    )}
                    <td className="px-3 py-2.5 whitespace-nowrap"><TxTypeBadge type={t.txType} /></td>
                    <td className="px-3 py-2.5 max-w-[180px]">
                      <p className="font-medium text-slate-800 truncate" title={t.itemName}>{t.itemName||'—'}</p>
                      {t.txType === 'material_damage' && t.remarks && (
                        <p className="text-xs text-red-500 truncate" title={t.remarks}>{t.remarks}</p>
                      )}
                      {(t.txType === 'material_edit' || t.txType === 'material_addition') && t.remarks && (
                        <p className="text-xs text-slate-400 truncate" title={t.remarks}>{t.remarks}</p>
                      )}
                      {t.txType === 'stationery_damage' && t.repairStatus && (
                        <p className="text-xs text-orange-500 truncate">{t.repairStatus.replace('_', ' ').toLowerCase()}</p>
                      )}
                      {t.txType === 'it_damage' && t.repairStatus && (
                        <p className="text-xs text-rose-500 truncate">{t.repairStatus.replace('_', ' ').toLowerCase()}</p>
                      )}
                      {t.txType === 'damage_approval' && t.remarks && (
                        <p className="text-xs text-amber-600 truncate" title={t.remarks}>{t.remarks}</p>
                      )}
                      {t.materialUnitId && (
                        <button
                          className="block font-mono text-xs text-indigo-600 hover:underline"
                          onClick={() => openMaterialHistory(t.materialUnitId)}
                        >
                          {t.materialIdCode}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2.5"><CategoryBadge category={t.category} /></td>
                    <td className="px-3 py-2.5 min-w-[120px]">
                      <p className="text-sm font-medium text-slate-700 leading-tight">{t.workerName||'—'}</p>
                      {t.workerEmployeeId && <p className="text-xs text-slate-400 leading-tight">{t.workerEmployeeId}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-700">
                      {t.quantity ?? '—'}
                      {t.beforeQty != null && t.afterQty != null && (
                        <p className="text-xs font-normal text-slate-400 whitespace-nowrap">({t.beforeQty} → {t.afterQty})</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-slate-500 whitespace-nowrap">{t.unitPrice>0?fmtVal(t.unitPrice):'—'}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-700 whitespace-nowrap">{t.value>0?fmtVal(t.value):'—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap"><StatusBadge status={t.status} /></td>
                    <td className="px-3 py-2.5 text-xs text-slate-400 whitespace-nowrap">{t.issuedByUsername||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && <Pagination page={page} total={totalPages} onChange={setPage} />}
      </div>

      {/* Material ID lifecycle history modal */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Material History</h3>
                {historyModal !== 'loading' && (
                  <p className="text-sm text-gray-500 mt-0.5">{historyModal.unit?.materialIdCode}</p>
                )}
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
            </div>
            {historyModal === 'loading' ? (
              <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">
                  {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name} · {historyModal.unit?.site?.name}
                </p>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {historyModal.events.map((e) => (
                    <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                        {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                        {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                      </div>
                      <p className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(e.occurredAt).toLocaleString('en-GB')}
                      </p>
                    </div>
                  ))}
                  {historyModal.events.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
                  )}
                </div>
                <MaterialInfoPanel materialUnitId={historyModal.unit?.id} unitKitId={historyModal.unit?.kitId} unitItItemId={historyModal.unit?.itItemId} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
